import { Routes } from '@angular/router';
import { authGuard } from './guards/auth-guard';
import { RoleGuard } from './guards/role.guard';
import { EntryPointComponent } from './views/entry-point/entry-point.component';
import { PermissionsEnum } from './enums/PermissionsEnum';
import { RolesEnum } from './enums/RolesEnum';
import { ViewPath } from './constants/ViewPath';

export const routes: Routes = [
  /** No Auth routes */
  {
    path: 'login',
    title: 'Backoffice | Login',
    loadComponent: () => import('./views/login/login').then((mod) => mod.Login),
  },

  {
    path: '',
    title: 'Backoffice',
    component: EntryPointComponent,
    canActivate: [authGuard, RoleGuard],
    children: [
      {
        path: '',
        children: [
          {
            path: 'other',
            title: 'Other',
            loadComponent: () =>
              import('./views/other-component/other-component').then((m) => m.OtherComponent),
          },
          {
            path: ViewPath.profile.path,
            title: 'Backoffice | Mi Perfil',
            loadComponent: () =>
              import('./views/user-details/user-details.component').then(
                (m) => m.UserDetailsComponent,
              ),
            data: {},
          },
          {
            path: ViewPath.clients.path,
            title: 'Backoffice | Subscripciones',
            loadComponent: () =>
              import('./views/subscriptions/subscriptions').then((m) => m.Subscriptions),
            data: {
              permissions: [PermissionsEnum.CLIENT_READ_LIST],
            },
          },
          {
            path: ViewPath.clientdetails.path,
            title: 'Backoffice | Detalle de Cliente',
            loadComponent: () =>
              import('./views/client-details/client-details.component').then(
                (m) => m.ClientDetailsComponent,
              ),
            data: {
              permissions: [PermissionsEnum.CLIENT_READ_DETAIL],
            },
          },
          {
            path: ViewPath.users.path,
            title: 'Backoffice | Usuarios',
            loadComponent: () => import('./views/users/users').then((m) => m.Users),
            data: {
              permissions: [PermissionsEnum.USER_READ_LIST],
            },
          },
          {
            path: ViewPath.userdetails.path,
            title: 'Backoffice | Detalle del Usuario',
            loadComponent: () =>
              import('./views/user-details/user-details.component').then(
                (m) => m.UserDetailsComponent,
              ),
            data: {
              permissions: [PermissionsEnum.USER_READ_DETAIL],
            },
          },
          {
            path: ViewPath.modules.path,
            title: 'Backoffice | Módulos',
            loadComponent: () => import('./views/modules/modules').then((m) => m.Modules),
            data: {
              permissions: [PermissionsEnum.MODULE_READ_LIST],
            },
          },
          {
            path: ViewPath.moduledetails.path,
            title: 'Backoffice | Módulo de compañía',
            loadComponent: () =>
              import('./views/module-details/module-details.component').then(
                (m) => m.ModuleDetails,
              ),
            data: {
              permissions: [PermissionsEnum.MODULE_READ_DETAIL],
            },
          },
          {
            path: ViewPath.billingCharges.path,
            title: 'Backoffice | Pre Facturas',
            loadComponent: () =>
              import('./views/billing-charges/billing-charges').then((m) => m.BillingCharges),
            data: {
              permissions: [PermissionsEnum.INVOICE_READ_LIST],
            },
          },
          {
            path: ViewPath.billingChargeDetails.path,
            title: 'Backoffice | Detalle de Pre Factura',
            loadComponent: () =>
              import('./views/billing-charge-details/billing-charge-details.component').then(
                (m) => m.BillingChargeDetailsComponent,
              ),
            data: {
              permissions: [PermissionsEnum.INVOICE_READ_DETAIL],
            },
          },
          {
            path: ViewPath.dashboard.path,
            title: 'Backoffice | Dashboard',
            loadComponent: () => import('./views/dashboard/dashboard').then((m) => m.Dashboard),
            data: {
              permissions: [PermissionsEnum.BACKOFFICE_READ_DASHBOARD],
            },
          },
          {
            path: ViewPath.commercialCalculator.path,
            title: 'Backoffice | Calculadora Comercial',
            loadComponent: () =>
              import('./views/commercial-calculator/commercial-calculator.component').then(
                (m) => m.CommercialCalculatorComponent,
              ),
          },
          {
            path: ViewPath.internalLogs.path,
            title: 'Backoffice | Logs Internos',
            loadComponent: () =>
              import('./views/internal-logs/internal-logs').then((m) => m.InternalLogsComponent),
            data: {
              permissions: [PermissionsEnum.LOG_READ_INTERNAL],
            },
          },
          {
            path: ViewPath.einaLogs.path,
            title: 'Backoffice | Logs de Eina',
            loadComponent: () =>
              import('./views/eina-logs/eina-logs').then((m) => m.EinaLogsComponent),
            data: {
              permissions: [PermissionsEnum.LOG_READ_EINA],
            },
          },
          {
            path: ViewPath.systemLogs.path,
            title: 'Backoffice | Logs de Sistema',
            loadComponent: () =>
              import('./views/system-logs/system-logs').then((m) => m.SystemLogsComponent),
            data: {
              permissions: [PermissionsEnum.LOG_READ_SYSTEM],
            },
          },
          {
            path: ViewPath.errorLogs.path,
            title: 'Backoffice | Logs de Error',
            loadComponent: () => import('./views/error-logs/error-logs').then((m) => m.ErrorLogs),
            data: {
              permissions: [PermissionsEnum.LOG_READ_ERROR],
            },
          },
          {
            path: ViewPath.feedback.path,
            title: 'Backoffice | Encuestas',
            loadComponent: () => import('./views/feedback/feedback').then((m) => m.Feedback),
          },
          {
            path: ViewPath.feedbackDetails.path,
            title: 'Backoffice | Detalle de Encuesta',
            loadComponent: () =>
              import('./views/feedback-details/feedback-details.component').then(
                (m) => m.FeedbackDetailsComponent,
              ),
          },
          {
            path: ViewPath.importStatus.path,
            title: 'Backoffice | Estado de Importaciones',
            loadComponent: () =>
              import('./views/import-status/import-status').then((m) => m.ImportStatus),
            data: {
              permissions: [PermissionsEnum.IMPORT_READ_STATUS],
            },
          },
          {
            path: ViewPath.endpointTimings.path,
            title: 'Backoffice | Rendimiento',
            loadComponent: () =>
              import('./views/endpoint-timings/endpoint-timings.component').then(
                (m) => m.EndpointTimingsComponent,
              ),
            data: {
              permissions: [PermissionsEnum.BACKOFFICE_READ_ENDPOINT_TIMINGS],
            },
          },
          {
            path: ViewPath.companyTokens.path,
            title: 'Backoffice | Tokens de compañía',
            loadComponent: () =>
              import('./views/company-tokens/company-tokens.component').then(
                (m) => m.CompanyTokensComponent,
              ),
          },
          {
            path: ViewPath.companiesContacts.path,
            title: 'Backoffice | Contactos',
            loadComponent: () =>
              import('./views/contacts/contacts.component').then((m) => m.ContactsComponent),
            data: { permissions: [PermissionsEnum.EINA_READ_CONTACTS] },
          },
          {
            path: ViewPath.companiesContactsDetails.path,
            title: ' Backoffice | Detalle contacto',
            loadComponent: () =>
              import('./views/siniwin/contact-details/contact-details.component').then(
                (m) => m.ContactDetailsComponent,
              ),
            data: { permissions: [PermissionsEnum.EINA_READ_CONTACT] },
          },
          {
            path: ViewPath.historyCalls.path,
            title: ' Backoffice | Historial llamadas',
            loadComponent: () =>
              import('./views/history-calls/history-calls.component').then(
                (m) => m.HistoryCallsComponent,
              ),
            data: { permissions: [PermissionsEnum.COMERCIAL_CALLS] },
          },
          {
            path: ViewPath.masterClients.path,
            title: 'Backoffice | Clientes Maestros',
            loadComponent: () =>
              import('./views/master-clients/master-clients.component').then(
                (m) => m.MasterClientsComponent,
              ),
            data: { permissions: [PermissionsEnum.MASTER_CLIENTS_READ_LIST] },
          },
          {
            path: ViewPath.masterClientsDetail.path,
            title: 'Backoffice | Detalle de Contacto Unificado',
            loadComponent: () =>
              import('./views/master-client-details/master-client-details.component').then(
                (m) => m.MasterClientDetailsComponent,
              ),
            data: { permissions: [PermissionsEnum.MASTER_CLIENT_READ_DETAIL] },
          },
        ],
      },
      {
        path: ViewPath.developerBranch.path,
        title: 'Backoffice | Ramas',
        loadComponent: () =>
          import('./views/developer/developer-branch/developer-branch').then(
            (m) => m.DeveloperBranchComponent,
          ),
        data: {
          permissions: [PermissionsEnum.DEVELOPER_READ_BRANCHES],
        },
      },
      {
        path: ViewPath.developerDatabase.path,
        title: 'Backoffice | Base de datos',
        loadComponent: () =>
          import('./views/developer/developer-database/developer-database').then(
            (m) => m.DeveloperDatabaseComponent,
          ),
        data: {
          permissions: [PermissionsEnum.DEVELOPER_READ_DATABASE],
        },
      },
    ],
  },

  {
    path: '**',
    pathMatch: 'full',
    title: 'Error',
    loadComponent: () =>
      import('./views/page-not-found/page-not-found').then((mod) => mod.PageNotFound),
  },
];
